'use client';

import { useState, useRef, useEffect } from 'react';
import { Play, Film } from 'lucide-react';
import { cn } from '../utils/cn';
import { S3Image } from './s3-image';

interface VideoThumbnailProps {
  src: string;
  poster?: string | null;
  alt?: string;
  className?: string;
  iconSize?: 'sm' | 'md' | 'lg';
  showOverlay?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  posterShowSkeleton?: boolean;
  posterFadeDuration?: number;
}

/**
 * Video thumbnail component that handles mobile browsers
 * where preload="metadata" doesn't work.
 */
export function VideoThumbnail({
  src,
  poster,
  alt,
  className,
  iconSize = 'md',
  showOverlay = true,
  autoPlay = false,
  loop = false,
  posterShowSkeleton = true,
  posterFadeDuration = 300,
}: VideoThumbnailProps) {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [hasPosterError, setHasPosterError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const iconSizes = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  };

  useEffect(() => {
    if (poster && !hasPosterError && !autoPlay) return;

    const video = videoRef.current;
    if (!video) return;

    if (video.readyState >= 2) {
      setIsVideoLoaded(true);
      return;
    }

    const handleLoadedData = () => setIsVideoLoaded(true);
    const handleError = () => setHasVideoError(true);

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);

    video.load();

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
    };
  }, [src, poster, hasPosterError, autoPlay]);

  const showPoster = poster && !hasPosterError && !autoPlay;
  const showVideo = (!showPoster && isVideoLoaded && !hasVideoError) || autoPlay;
  const showPlaceholder = !showPoster && !showVideo;

  return (
    <div className={cn('relative h-full w-full', className)}>
      {showPoster && (
        <S3Image
          src={poster}
          alt={alt || 'Video thumbnail'}
          fill
          className="object-cover"
          showSkeleton={posterShowSkeleton}
          fadeDuration={posterFadeDuration}
          onError={() => setHasPosterError(true)}
        />
      )}

      {(autoPlay || !showPoster) && (
        <video
          ref={videoRef}
          src={src}
          className={cn(
            'h-full w-full object-cover transition-opacity duration-200',
            showVideo ? 'opacity-100' : 'opacity-0'
          )}
          muted
          playsInline
          autoPlay={autoPlay}
          loop={loop}
          preload={autoPlay ? 'auto' : 'metadata'}
          aria-label={alt}
        />
      )}

      {showPlaceholder && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 via-secondary to-primary/10">
          <Film className={cn(iconSizes[iconSize], 'text-primary/60')} />
        </div>
      )}

      {showOverlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
            <Play className={cn(iconSizes[iconSize], 'text-white')} />
          </div>
        </div>
      )}
    </div>
  );
}
